import { isNumber, isObject, isString } from "lodash-es";
import { Upload, DetailedError, type HttpRequest, type HttpResponse } from "tus-js-client";
import { api } from "@/modules/api";
import { clientEnvModule } from "@/modules/client-env";
import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import { GLOBAL_API_CLIENT } from "@/modules/api/globalApiClient";
import { getNetworkInfo } from "@/domains/network/status";
import { getFileExtensionFromMimeType, normalizeMimeType } from "./mime-utils";
import { buildCombinedUploadErrorMessage, generateTusFingerprint } from "./tus-upload-utils";
import type { TranscriptArrayItem } from "@/modules/audio-recording/transcript-types";
import type { AudioUploadMergeIntentPayload } from "@/store/sync/operations/types";
import { truncate } from "@/modules/truncate/truncate";

const TUS_ENDPOINT = "https://storage-api.mem.run/files";
const TUS_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
const FINALIZE_UPLOAD_TIMEOUT_MS = 60_000;
const FINALIZE_PROGRESS_START = 90;
const FINALIZE_PROGRESS_MAX = 99;
const FINALIZE_PROGRESS_DURATION_MS = 10_000;
const FINALIZE_PROGRESS_INTERVAL_MS = 500;
const TUS_PROGRESS_SCALE_BASE = 100;

enum TranscriptProvider {
  AssemblyAiStreaming = "ASSEMBLY_AI_STREAMING",
  AppleSpeech = "APPLE_SPEECH",
}

const DEFAULT_TRANSCRIPT_PROVIDER: TranscriptProvider = TranscriptProvider.AssemblyAiStreaming;

function buildAssociatedTranscriptData(params: {
  transcriptText?: string;
  transcriptArray?: TranscriptArrayItem[];
  transcriptProvider?: TranscriptProvider | null;
}): {
  transcript_text: string | null;
  transcript_array: TranscriptArrayItem[] | null;
  transcript_provider: TranscriptProvider | null;
} | null {
  const transcriptText = params.transcriptText?.trim() || null;
  const transcriptArray = params.transcriptArray?.length ? params.transcriptArray : null;

  if (!transcriptText && !transcriptArray) return null;

  return {
    transcript_text: transcriptText,
    transcript_array: transcriptArray,
    transcript_provider: params.transcriptProvider ?? DEFAULT_TRANSCRIPT_PROVIDER,
  };
}

export interface UploadMeta {
  capturedAudioId: string;
  associatedAudioRecordingId: string;
  associatedNoteId?: string | null;
  associatedSessionId?: string | null;
  transcriptText: string;
  transcriptArray: TranscriptArrayItem[];
  preferredMimeType?: string;
  audioRecordingMergeIntent?: AudioUploadMergeIntentPayload;
}

export type UploadResult = { success: true } | { success: false; error: string };
type TusUploadResult =
  | { success: true; priorTransportError: string | null }
  | { success: false; error: string; priorTransportError: string | null };
type TusRetryFailureContext = {
  message: string;
  status: number;
  method: string;
  path: string;
};

interface ProgressReporterConfig {
  onProgress: ((percent: number, loaded: number, total: number) => void) | undefined;
  totalBytes: number;
  audioRecordingId: string;
  filename: string;
}

interface FinalizeProgressRampConfig {
  onProgress: (percent: number) => void;
  startPercent: number;
  maxPercent: number;
  durationMs: number;
  intervalMs: number;
  logContext: { audioRecordingId: string; filename: string };
}

export class UploadPauser {
  private paused = false;
  private waiters: Array<() => void> = [];

  pause(): void {
    this.paused = true;
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.waiters.splice(0).forEach(resolve => resolve());
  }

  isPaused(): boolean {
    return this.paused;
  }

  async waitIfPaused(): Promise<void> {
    if (!this.paused) return;
    await new Promise<void>(resolve => this.waiters.push(resolve));
  }
}

export async function uploadRecording(
  blob: Blob | null,
  meta: UploadMeta,
  opts?: {
    onProgress?: (percent: number, loaded: number, total: number) => void;
    signal?: AbortSignal;
    pauser?: UploadPauser;
    forceRestart?: boolean;
  }
): Promise<UploadResult> {
  const uploadStartTime = Date.now();
  const uploadAttemptId = uuidModule.generate();
  const mimeType = normalizeMimeType({ mimeTypeStr: blob?.type || meta.preferredMimeType });
  let priorTransportError: string | null = null;

  if (!blob) return { success: false, error: "No audio recording available to upload" };
  if (!mimeType) return { success: false, error: "No supported MIME type found" };

  const fileExtension = getFileExtensionFromMimeType({ mimeTypeStr: mimeType });
  const filename = `${meta.associatedAudioRecordingId}.${fileExtension}`;
  const progressReporter = createUploadProgressReporter({
    onProgress: opts?.onProgress,
    totalBytes: blob.size,
    audioRecordingId: meta.associatedAudioRecordingId,
    filename,
  });

  try {
    logger.info({
      message: "[tus-upload-service] upload start",
      info: {
        uploadAttemptId,
        audioRecordingId: meta.associatedAudioRecordingId,
        filename,
        mimeType,
        sizeBytes: blob.size,
        clientVersion: clientEnvModule.packageVersion(),
      },
    });

    // Register upload
    const registerStartTime = Date.now();
    const registerResult = await registerUpload({
      uploadAttemptId,
      audioRecordingId: meta.associatedAudioRecordingId,
      filename,
      mimeType,
      transcriptText: meta.transcriptText,
      transcriptArray: meta.transcriptArray,
    });

    if (!registerResult.success) {
      return { success: false, error: registerResult.error };
    }

    logger.info({
      message: "[tus-upload-service] registerUpload complete",
      info: {
        uploadAttemptId,
        audioRecordingId: meta.associatedAudioRecordingId,
        normalizedFilename: registerResult.data.normalized_filename,
        bucketName: registerResult.data.bucket_name,
        spaceAccountId: registerResult.data.space_account_id,
        durationMs: Date.now() - registerStartTime,
      },
    });

    // Upload via TUS
    const tusStartTime = Date.now();
    const tusResult = await uploadViaTus({
      uploadAttemptId,
      file: blob,
      mimeType,
      filename: registerResult.data.normalized_filename,
      spaceAccountId: registerResult.data.space_account_id,
      audioRecordingId: meta.associatedAudioRecordingId,
      bucketName: registerResult.data.bucket_name,
      onProgress: progressReporter.reportTusProgress,
      signal: opts?.signal,
      forceRestart: opts?.forceRestart,
    });
    priorTransportError = tusResult.priorTransportError;

    if (!tusResult.success) {
      return {
        success: false,
        error: buildCombinedUploadErrorMessage({
          primaryError:
            tusResult.error ??
            buildUploadErrorMessage({ source: "tus", message: "TUS upload failed" }),
          priorTransportError,
        }),
      };
    }

    logger.info({
      message: "[tus-upload-service] tus upload completed",
      info: {
        uploadAttemptId,
        audioRecordingId: meta.associatedAudioRecordingId,
        filename: registerResult.data.normalized_filename,
        durationMs: Date.now() - tusStartTime,
      },
    });

    // Finalize upload
    const finalizeStartTime = Date.now();
    logger.info({
      message: "[tus-upload-service] finalizeUpload start",
      info: {
        uploadAttemptId,
        audioRecordingId: meta.associatedAudioRecordingId,
        filename: registerResult.data.normalized_filename,
      },
    });
    progressReporter.startFinalizeProgress();
    let finalizeResult: FinalizeUploadResult = {
      success: false,
      error: buildUploadErrorMessage({
        source: "upload_api/finalize",
        message: "Finalize upload failed",
        details: {
          endpoint: "/v2/audio-recordings/{audio_recording_id}/finalize-upload",
          timeoutMs: FINALIZE_UPLOAD_TIMEOUT_MS,
        },
      }),
    };
    try {
      finalizeResult = await finalizeUpload({
        uploadAttemptId,
        audioRecordingId: meta.associatedAudioRecordingId,
        filename: registerResult.data.normalized_filename,
        mimeType,
        associatedNoteId: meta.associatedNoteId,
        associatedSessionId: meta.associatedSessionId,
        transcriptText: meta.transcriptText,
        transcriptArray: meta.transcriptArray,
        audioRecordingMergeIntent: meta.audioRecordingMergeIntent,
      });
    } finally {
      progressReporter.stopFinalizeProgress();
    }

    if (!finalizeResult.success) {
      return {
        success: false,
        error: buildCombinedUploadErrorMessage({
          primaryError: finalizeResult.error,
          priorTransportError,
        }),
      };
    }

    progressReporter.complete();
    logger.info({
      message: "[tus-upload-service] finalizeUpload complete",
      info: {
        uploadAttemptId,
        audioRecordingId: meta.associatedAudioRecordingId,
        filename: registerResult.data.normalized_filename,
        durationMs: Date.now() - finalizeStartTime,
        totalDurationMs: Date.now() - uploadStartTime,
      },
    });

    return { success: true };
  } catch (error: unknown) {
    progressReporter.stopFinalizeProgress();
    const errorMessage = buildCombinedUploadErrorMessage({
      primaryError: buildUploadErrorMessage({
        source: "upload",
        message: error instanceof Error ? error.message : "Upload failed",
      }),
      priorTransportError,
    });
    logger.error({
      message: "[tus-upload-service] Upload failed",
      info: {
        uploadAttemptId,
        error: errorMessage,
        audioRecordingId: meta.associatedAudioRecordingId,
      },
    });
    return { success: false, error: errorMessage };
  }
}

function createUploadProgressReporter({
  onProgress,
  totalBytes,
  audioRecordingId,
  filename,
}: ProgressReporterConfig) {
  let lastReported = 0;
  const finalizeRamp = createFinalizeProgressRamp({
    onProgress: (percent: number) => report(percent),
    startPercent: FINALIZE_PROGRESS_START,
    maxPercent: FINALIZE_PROGRESS_MAX,
    durationMs: FINALIZE_PROGRESS_DURATION_MS,
    intervalMs: FINALIZE_PROGRESS_INTERVAL_MS,
    logContext: { audioRecordingId, filename },
  });

  function report(percent: number, loaded = totalBytes, total = totalBytes) {
    if (!onProgress) return;
    const clamped = Math.max(lastReported, Math.min(100, Math.round(percent)));
    if (clamped === lastReported) return;
    lastReported = clamped;
    onProgress(clamped, loaded, total);
  }

  const reportTusProgress = (percent: number, loaded: number, total: number) => {
    // Scale tus progress (0-100) to 0-FINALIZE_PROGRESS_START (e.g., 0-90%)
    const scaled = Math.min(
      FINALIZE_PROGRESS_START,
      Math.floor(percent * (FINALIZE_PROGRESS_START / TUS_PROGRESS_SCALE_BASE))
    );
    report(scaled, loaded, total);
  };

  const startFinalizeProgress = () => {
    finalizeRamp.start();
  };

  const stopFinalizeProgress = () => {
    finalizeRamp.stop();
  };

  const complete = () => {
    finalizeRamp.stop();
    report(100);
  };

  return { reportTusProgress, startFinalizeProgress, stopFinalizeProgress, complete };
}

function createFinalizeProgressRamp({
  onProgress,
  startPercent,
  maxPercent,
  durationMs,
  intervalMs,
  logContext,
}: FinalizeProgressRampConfig) {
  let timer: ReturnType<typeof setInterval> | null = null;
  let startedAt: number | null = null;

  const tick = () => {
    if (startedAt === null) return;
    const elapsed = Date.now() - startedAt;
    const ratio = Math.min(elapsed / durationMs, 1);
    const target = startPercent + Math.floor(ratio * (maxPercent - startPercent));
    onProgress(target);
    if (ratio >= 1 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const start = () => {
    if (timer) return;
    startedAt = Date.now();
    logger.info({
      message: "[tus-upload-service] finalize progress ramp start",
      info: { ...logContext },
    });
    timer = setInterval(tick, intervalMs);
    tick();
  };

  const stop = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
      logger.info({
        message: "[tus-upload-service] finalize progress ramp stop",
        info: { ...logContext },
      });
    }
    startedAt = null;
  };

  return { start, stop };
}

interface RegisterUploadResponse {
  bucket_name: string;
  space_account_id: string;
  audio_recording_id: string;
  normalized_filename: string;
}

type RegisterUploadResult =
  | { success: true; data: RegisterUploadResponse }
  | { success: false; error: string };

type FinalizeUploadResult = { success: true } | { success: false; error: string };

async function registerUpload(params: {
  uploadAttemptId?: string;
  audioRecordingId: string;
  filename: string;
  mimeType: string;
  transcriptText?: string;
  transcriptArray?: TranscriptArrayItem[];
}): Promise<RegisterUploadResult> {
  try {
    const response = await api.post("/v2/audio-recordings/{audio_recording_id}/register-upload", {
      params: { path: { audio_recording_id: params.audioRecordingId } },
      body: {
        filename: params.filename,
        mime_type: params.mimeType,
        associated_transcript_data: buildAssociatedTranscriptData({
          transcriptText: params.transcriptText,
          transcriptArray: params.transcriptArray,
        }),
      },
    });
    if (!response.data) {
      return {
        success: false,
        error: buildUploadErrorMessage({
          source: "upload_api/register",
          message: "Register upload response missing data",
          details: {
            endpoint: "/v2/audio-recordings/{audio_recording_id}/register-upload",
          },
        }),
      };
    }
    return { success: true, data: response.data };
  } catch (error) {
    const errorMessage = buildUploadErrorMessage({
      source: "upload_api/register",
      message: error instanceof Error ? error.message : String(error),
      details: {
        endpoint: "/v2/audio-recordings/{audio_recording_id}/register-upload",
      },
    });
    logger.error({
      message: "[tus-upload-service] registerUpload failed",
      info: {
        uploadAttemptId: params.uploadAttemptId ?? null,
        audioRecordingId: params.audioRecordingId,
        error: errorMessage,
      },
    });
    return { success: false, error: errorMessage };
  }
}

async function finalizeUpload(params: {
  uploadAttemptId?: string;
  audioRecordingId: string;
  filename: string;
  mimeType: string;
  associatedNoteId?: string | null;
  associatedSessionId?: string | null;
  transcriptText?: string;
  transcriptArray?: TranscriptArrayItem[];
  audioRecordingMergeIntent?: AudioUploadMergeIntentPayload;
}): Promise<FinalizeUploadResult> {
  try {
    const requestBody = {
      filename: params.filename,
      mime_type: params.mimeType,
      associated_note_id: params.associatedNoteId ?? undefined,
      associated_session_id: params.associatedSessionId ?? undefined,
      associated_transcript_data: buildAssociatedTranscriptData({
        transcriptText: params.transcriptText,
        transcriptArray: params.transcriptArray,
      }),
      audio_recording_merge_intent: params.audioRecordingMergeIntent,
      source_creation_strategy: "local" as const,
    };

    const response = await withTimeout(
      api.post("/v2/audio-recordings/{audio_recording_id}/finalize-upload", {
        params: { path: { audio_recording_id: params.audioRecordingId } },
        body: requestBody,
      }),
      FINALIZE_UPLOAD_TIMEOUT_MS,
      "Finalize upload timed out"
    );
    if (!response.data?.success) {
      return {
        success: false,
        error: buildUploadErrorMessage({
          source: "upload_api/finalize",
          message: "Finalize upload response was unsuccessful",
          details: {
            endpoint: "/v2/audio-recordings/{audio_recording_id}/finalize-upload",
            timeoutMs: FINALIZE_UPLOAD_TIMEOUT_MS,
          },
        }),
      };
    }
    return { success: true };
  } catch (error) {
    const errorMessage = buildUploadErrorMessage({
      source: "upload_api/finalize",
      message: error instanceof Error ? error.message : String(error),
      details: {
        endpoint: "/v2/audio-recordings/{audio_recording_id}/finalize-upload",
        timeoutMs: FINALIZE_UPLOAD_TIMEOUT_MS,
      },
    });
    logger.error({
      message: "[tus-upload-service] finalizeUpload failed",
      info: {
        uploadAttemptId: params.uploadAttemptId ?? null,
        audioRecordingId: params.audioRecordingId,
        error: errorMessage,
      },
    });
    return { success: false, error: errorMessage };
  }
}

async function uploadViaTus(params: {
  uploadAttemptId: string;
  file: Blob;
  mimeType: string;
  filename: string;
  spaceAccountId: string;
  audioRecordingId: string;
  bucketName: string;
  onProgress?: (percent: number, loaded: number, total: number) => void;
  signal?: AbortSignal;
  forceRestart?: boolean;
}): Promise<TusUploadResult> {
  const authToken = GLOBAL_API_CLIENT.getAuthToken();
  if (!authToken) {
    return {
      success: false,
      error: buildUploadErrorMessage({
        source: "tus/auth",
        message: "No auth token available",
        details: { reason: "missing_auth" },
      }),
      priorTransportError: null,
    };
  }

  return new Promise(resolve => {
    const tusStartTime = Date.now();
    let loggedUrl = false;
    let previousUploadsCount = 0;
    let previousUploadPath: string | null = null;
    let clearedUploadsCount = 0;
    let priorTransportError: string | null = null;
    const networkInfo = getNetworkInfo();
    const clientVersion = clientEnvModule.packageVersion();
    const headers: Record<string, string> = {
      Authorization: `OAuthAccessToken ${authToken}`,
      "X-Mem-Upload-Attempt": params.uploadAttemptId,
    };
    if (clientVersion) {
      headers["X-Mem-Client-Version"] = clientVersion;
    }
    const upload = new Upload(params.file, {
      endpoint: TUS_ENDPOINT,
      uploadSize: params.file.size,
      chunkSize: TUS_CHUNK_SIZE,
      retryDelays: [0, 1000, 3000, 7000, 15000],
      removeFingerprintOnSuccess: true,
      fingerprint: async (file, options) => {
        return generateTusFingerprint({
          audioRecordingId: params.audioRecordingId,
          filename: params.filename,
          fileSize: typeof file.size === "number" ? file.size : "unknown",
          endpoint: options.endpoint ?? "unknown",
        });
      },
      metadata: {
        filename: params.filename,
        filetype: params.mimeType,
        space_account_id: params.spaceAccountId,
        file_id: params.audioRecordingId,
        bucket_name: params.bucketName,
        is_chunk: "false",
        content_length: String(params.file.size),
      },
      headers,
      onUploadUrlAvailable: () => {
        if (loggedUrl) return;
        loggedUrl = true;
        const uploadPath = safePathFromUrl({ url: upload.url });
        logger.info({
          message: "[tus-upload-service] tus upload url available",
          info: {
            uploadAttemptId: params.uploadAttemptId ?? null,
            audioRecordingId: params.audioRecordingId,
            uploadPath: uploadPath ?? null,
            previousUploadsCount,
            previousUploadPath,
          },
        });
      },
      onProgress: (bytesSent: number, bytesTotal: number) => {
        if (params.onProgress && bytesTotal > 0) {
          params.onProgress(Math.floor((bytesSent / bytesTotal) * 100), bytesSent, bytesTotal);
        }
      },
      onAfterResponse: (request, response) => {
        const retryFailure = buildTusRetryFailureContext({ request, response });
        if (!retryFailure) {
          return;
        }

        priorTransportError = retryFailure.message;

        logger.warn({
          message: "[tus-upload-service] transient tus failure",
          info: {
            uploadAttemptId: params.uploadAttemptId,
            audioRecordingId: params.audioRecordingId,
            filename: params.filename,
            status: retryFailure.status,
            method: retryFailure.method,
            path: retryFailure.path,
            priorTransportError,
          },
        });
      },
      onSuccess: () => {
        logger.info({
          message: "[tus-upload-service] tus upload success",
          info: {
            uploadAttemptId: params.uploadAttemptId,
            audioRecordingId: params.audioRecordingId,
            filename: params.filename,
            durationMs: Date.now() - tusStartTime,
            previousUploadsCount,
            previousUploadPath,
          },
        });
        resolve({ success: true, priorTransportError });
      },
      onError: (error: Error | DetailedError) => {
        const requestInfo = extractTusRequestInfo({ error });
        const message = buildUploadErrorMessage({
          source: "tus",
          message: error.message ?? "TUS upload failed",
          details: {
            status: requestInfo?.status,
            statusText: requestInfo?.statusText,
            method: requestInfo?.method,
            path: requestInfo?.urlPath,
            readyState: requestInfo?.readyState,
            online: networkInfo.online ?? undefined,
            effectiveType: networkInfo.effectiveType,
          },
        });
        logger.error({
          message: "[tus-upload-service] tus upload error",
          info: {
            uploadAttemptId: params.uploadAttemptId,
            audioRecordingId: params.audioRecordingId,
            filename: params.filename,
            error: message,
            errorName: error instanceof Error ? error.name : null,
            durationMs: Date.now() - tusStartTime,
            previousUploadsCount,
            previousUploadPath,
            requestInfo,
            networkInfo,
          },
        });
        resolve({ success: false, error: message, priorTransportError });
      },
    });

    if (params.signal) {
      if (params.signal.aborted) {
        resolve({
          success: false,
          error: buildUploadErrorMessage({
            source: "tus/abort",
            message: "Upload aborted",
            details: { reason: "signal_aborted" },
          }),
          priorTransportError,
        });
        return;
      }
      params.signal.addEventListener("abort", () => {
        upload.abort(true);
        resolve({
          success: false,
          error: buildUploadErrorMessage({
            source: "tus/abort",
            message: "Upload aborted",
            details: { reason: "signal_aborted" },
          }),
          priorTransportError,
        });
      });
    }

    void (async () => {
      try {
        const previousUploads = await upload.findPreviousUploads();
        previousUploadsCount = previousUploads.length;
        if (params.forceRestart && previousUploads.length > 0) {
          const urlStorage = upload.options.urlStorage;
          if (urlStorage) {
            await Promise.all(
              previousUploads.map(previousUpload =>
                urlStorage.removeUpload(previousUpload.urlStorageKey)
              )
            );
            clearedUploadsCount = previousUploads.length;
          }
        }

        if (!params.forceRestart && previousUploads.length > 0) {
          const safePath = safePathFromUrl({ url: previousUploads[0]?.uploadUrl });
          previousUploadPath = safePath ?? null;
        } else {
          previousUploadPath = null;
        }
        logger.info({
          message: "[tus-upload-service] tus resume candidates",
          info: {
            uploadAttemptId: params.uploadAttemptId,
            audioRecordingId: params.audioRecordingId,
            previousUploadsCount,
            previousUploadPath,
            forceRestart: Boolean(params.forceRestart),
            clearedUploadsCount,
          },
        });
      } catch (error) {
        logger.warn({
          message: "[tus-upload-service] tus resume lookup failed",
          info: {
            uploadAttemptId: params.uploadAttemptId,
            audioRecordingId: params.audioRecordingId,
            error: error instanceof Error ? error.message : String(error),
          },
        });
      }
      upload.start();
    })();
  });
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  if (!timeoutMs) return promise;

  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  });
}

function buildTusRetryFailureContext({
  request,
  response,
}: {
  request: HttpRequest;
  response: HttpResponse;
}): TusRetryFailureContext | null {
  const status = response.getStatus();
  if (status < 400) {
    return null;
  }

  const path = safePathFromUrl({ url: request.getURL() }) ?? request.getURL();
  const method = request.getMethod();
  const statusText = response.getBody().trim() || "unexpected response";

  return {
    message: buildUploadErrorMessage({
      source: "tus/retry",
      message: `${method} ${path}: ${statusText}`,
      details: { status },
    }),
    status,
    method,
    path,
  };
}

function safePathFromUrl({ url }: { url: unknown }): string | undefined {
  if (!isString(url)) {
    return undefined;
  }
  if (url.length === 0) {
    return undefined;
  }
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

type TusRequestInfo = {
  method?: string;
  urlPath?: string;
  status?: number;
  statusText?: string;
  readyState?: number;
  responseText?: string;
  causingErrorType?: string;
  causingErrorMessage?: string;
  progressEventType?: string;
  progressEventLoaded?: number;
  progressEventTotal?: number;
  errorKeys?: string[];
};

type TusRequestLike = {
  method?: string;
  url?: string;
  status?: number;
  statusText?: string;
  readyState?: number;
  responseText?: string;
};

function extractTusRequestInfo({ error }: { error: unknown }): TusRequestInfo | null {
  if (!isObject(error)) {
    return null;
  }

  const info: TusRequestInfo = {};
  info.errorKeys = Object.keys(error);

  const request = getTusRequest({ error });
  if (request) {
    info.method = request.method;
    info.urlPath = safePathFromUrl({ url: request.url });
    info.status = request.status;
    info.statusText = request.statusText;
    info.readyState = request.readyState;
    info.responseText = buildTruncatedText({ value: request.responseText, max: 200 });
  }

  const causingError = extractCausingError({ error });
  if (causingError) {
    info.causingErrorType = causingError.constructor?.name ?? typeof causingError;
    if (causingError instanceof Error) {
      info.causingErrorMessage = causingError.message;
    }
    if (typeof ProgressEvent !== "undefined" && causingError instanceof ProgressEvent) {
      info.progressEventType = causingError.type;
      info.progressEventLoaded = causingError.loaded;
      info.progressEventTotal = causingError.total;
    }
  }

  return info;
}

function extractCausingError({ error }: { error: unknown }): unknown {
  if (!isObject(error)) {
    return null;
  }
  const candidate = error as {
    causingError?: unknown;
    originalError?: unknown;
    cause?: unknown;
  };
  return candidate.causingError ?? candidate.originalError ?? candidate.cause ?? null;
}

function getTusRequest({ error }: { error: unknown }): TusRequestLike | null {
  if (!isObject(error)) {
    return null;
  }
  const candidate = error as {
    originalRequest?: TusRequestLike;
    request?: TusRequestLike;
  };
  if (candidate.originalRequest) {
    return candidate.originalRequest;
  }
  if (candidate.request) {
    return candidate.request;
  }
  return null;
}

function buildTruncatedText({ value, max }: { value: unknown; max: number }): string | undefined {
  if (!isString(value)) {
    return undefined;
  }
  if (!isNumber(max) || max <= 0) {
    return undefined;
  }
  return truncate(value, max);
}

const MAX_UPLOAD_ERROR_SUMMARY_PAIRS = 8;
const MAX_UPLOAD_ERROR_VALUE_LENGTH = 120;

function buildUploadErrorMessage({
  source,
  message,
  details,
}: {
  source: string;
  message: string;
  details?: Record<string, string | number | boolean | null | undefined>;
}): string {
  const trimmed = message?.trim();
  const base = trimmed && trimmed.length > 0 ? trimmed : "Upload failed";
  const hasAnyPrefix = /^\[[^\]]+\]/.test(base);
  const prefixed = base.startsWith(`[${source}]`) || hasAnyPrefix ? base : `[${source}] ${base}`;
  const summary = buildUploadSummary(details);
  return summary ? `${prefixed} (${summary})` : prefixed;
}

function buildUploadSummary(
  details?: Record<string, string | number | boolean | null | undefined>
): string | undefined {
  if (!details) return undefined;
  const entries = Object.entries(details)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => {
      const rendered = String(value);
      const truncated =
        rendered.length > MAX_UPLOAD_ERROR_VALUE_LENGTH
          ? `${rendered.slice(0, MAX_UPLOAD_ERROR_VALUE_LENGTH - 3)}...`
          : rendered;
      return `${key}=${truncated}`;
    })
    .slice(0, MAX_UPLOAD_ERROR_SUMMARY_PAIRS);
  return entries.length ? entries.join("; ") : undefined;
}
